import React, { useEffect, useRef, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import Lozenge from '@atlaskit/lozenge';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
  ContextualAnalyticsData,
  FireScreenAnalytics,
  SCREEN,
} from '@atlassian/analytics-bridge';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import {
  WaitingForDeployment,
  DeploymentsOnboarding,
} from 'src/components/pipelines/components/onboarding';
import { DeploymentsWebsocketEvents } from 'src/components/pipelines/constants';
import { Allowance, EditorEnum } from 'src/components/pipelines/models';
import { Editor } from 'src/components/pipelines/pages/pipelines/editor';
import OnboardingCommit from 'src/components/pipelines/pages/pipelines/onboarding-commit';
import {
  requestBuildConfiguration,
  requestOfficialTemplates,
  setValidator,
  verifyPipelinesInstallation,
  webSocketSubscribe,
  webSocketUnsubscribe,
} from 'src/components/pipelines/redux/actions/pipelines';
import {
  getAccount,
  getBuildConfiguration,
  getCapabilities,
  getRepository,
  getValidator,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { loadAllowanceResource } from 'src/components/pipelines/resources/allowance';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { publishTrackEvent } from 'src/utils/analytics/publish';

const OnboardingWrapper = styled.div({
  padding: '20px',
});

const ValidatorHeader = styled.header({
  border: `1px ${token('color.border', colors.N50)} solid`,
  background: token('color.background.input', colors.N20),
  borderBottomWidth: 0,
  borderRadius: '3px 3px 0 0',
  color: token('color.text', colors.N800),
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'flex-end',
  h4: {
    font: token('font.body'),
    marginRight: '8px',
  },
});

const EditorWrapper = styled.div({
  border: `1px solid ${token('color.border', colors.N40)}`,
  '& > div': {
    height: 'calc(100vh - 800px)',
  },
});

const ValidatorSection = styled.div({
  '.YmlValidator__editorContent': {
    border: `1px ${token('color.border', colors.N40)} solid`,
    borderRadius: '0 0 0 3px',
  },
  '.YmlValidator__gutterRight': {
    border: `1px ${token('color.border', colors.N40)} solid`,
    borderRadius: '0 0 3px 0',
    borderLeftWidth: 0,
  },
  '.YmlValidator__panel': {
    borderColor: token('color.border', colors.N40),
  },
});

const Onboarding = () => {
  const dispatch = useDispatch();

  const account = useSelector(getAccount);
  const buildConfiguration = useSelector(getBuildConfiguration);

  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const validator = useSelector(getValidator);

  const [showWaitingPage, setShowWaitingPage] = useState(false);

  const isBitbucketPipelinesYmlSupportEnabled = useStatsigGate(
    StatsigFeatureKeys.isBitbucketPipelinesYmlSupportEnabled
  ) as boolean;

  const { data: allowanceData } = useResource(loadAllowanceResource);

  const defaultAllowance = new Allowance();
  const allowance = allowanceData ?? defaultAllowance;

  const source = 'deploymentsGettingStartedScreen';

  useEffect(() => {
    dispatch(verifyPipelinesInstallation());
    dispatch(setValidator({ currentEditor: EditorEnum.DeploymentsOnboarding }));
    dispatch(requestOfficialTemplates());
    dispatch(
      requestBuildConfiguration('', isBitbucketPipelinesYmlSupportEnabled)
    );
    DeploymentsWebsocketEvents.forEach(e => dispatch(webSocketSubscribe(e)));
    return () => {
      DeploymentsWebsocketEvents.forEach(e =>
        dispatch(webSocketUnsubscribe(e))
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCommiting = useRef(false);
  useEffect(() => {
    if (buildConfiguration.isCommiting && !isCommiting.current) {
      isCommiting.current = true;
    } else if (isCommiting.current && !buildConfiguration.isCommiting) {
      isCommiting.current = false;
      if (buildConfiguration.isCommitingError) {
        publishTrackEvent({
          actionSubjectId: '',
          actionSubject: 'configuration',
          action: 'failed',
          attributes: {
            error:
              buildConfiguration.isCommitingError?.error?.body?.error?.message,
          },
          source,
        });
      } else {
        setShowWaitingPage(true);
      }
    }
  }, [
    isCommiting,
    buildConfiguration,
    validator,
    source,
    repository,
    setShowWaitingPage,
  ]);

  if (showWaitingPage) {
    return <WaitingForDeployment repository={repository} />;
  }

  return (
    <ContextualAnalyticsData
      sourceName="deploymentsGettingStarted"
      sourceType={SCREEN}
    >
      <FireScreenAnalytics attributes={{ repositoryUuid: repository.uuid }} />
      <OnboardingWrapper data-testid="onboarding">
        <DeploymentsOnboarding
          account={account}
          allowance={allowance}
          capabilities={capabilities}
          repository={repository}
        />

        <ValidatorSection>
          <ValidatorHeader>
            <h4>bitbucket-pipelines.yml</h4>
            <Lozenge appearance="inprogress">Editable</Lozenge>
          </ValidatorHeader>
          <EditorWrapper>
            <Editor />
          </EditorWrapper>
        </ValidatorSection>
        <OnboardingCommit source={source} />
      </OnboardingWrapper>
    </ContextualAnalyticsData>
  );
};

export default React.memo(Onboarding);
